import FlyoutMenu from '@/components/layout/FlyoutMenu'
import {
  CameraIcon,
  ChevronDownIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline'

import { TaskType } from '@droidmap/project-service-contract'

export interface TaskTypeOption {
  type: TaskType
  title: string
  description: string
  icon: React.ComponentType<{ className?: string }>
}

/**
 * Canonical task-type metadata (icon, title, description) — single source of
 * truth shared by the selection cards and the New Task dropdown.
 */
export const taskTypeOptions: TaskTypeOption[] = [
  {
    type: TaskType.Photogrammetry,
    title: 'Photogrammetry',
    description:
      'Process drone images to generate orthomosaics, 3D models, point clouds, and digital elevation models.',
    icon: CameraIcon,
  },
  {
    type: TaskType.GCPTagging,
    title: 'GCP Tagging',
    description:
      'Create or import Ground Control Points (GCPs) and tag them on drone images for improved georeferencing accuracy.',
    icon: MapPinIcon,
  },
]

/**
 * Renders the task-type options as dropdown menu rows — shared by the New Task
 * button and the in-flow Change menu. Returns an array so FlyoutMenu treats each
 * row as a separate menu item.
 */
export const taskTypeMenuItems = (
  onSelect: (taskType: TaskType) => void,
  excludeType?: TaskType,
) =>
  taskTypeOptions
    .filter((option) => option.type !== excludeType)
    .map((option) => {
      const Icon = option.icon
      return (
        <button
          key={option.type}
          type="button"
          onClick={() => onSelect(option.type)}
          className="hover:bg-panel-2 flex w-80 items-start gap-3 rounded-md p-2 text-left transition-colors"
        >
          <Icon className="text-text-dim mt-0.5 h-5 w-5 shrink-0" />
          <span className="min-w-0">
            <span className="text-text block text-sm font-medium">
              {option.title}
            </span>
            <span className="text-text-dim block text-xs">
              {option.description}
            </span>
          </span>
        </button>
      )
    })

interface SelectedTaskTypeBarProps {
  selectedType: TaskType
  onSelect: (taskType: TaskType) => void
}

/**
 * Compact replacement for the full type cards once a type is chosen — keeps the
 * selection visible and switchable (via the Change dropdown) without the cards'
 * vertical footprint.
 */
export const SelectedTaskTypeBar = ({
  selectedType,
  onSelect,
}: SelectedTaskTypeBarProps) => {
  const option = taskTypeOptions.find((o) => o.type === selectedType)
  if (!option) return null
  const Icon = option.icon

  return (
    <div className="border-border bg-panel flex items-center gap-4 rounded-lg border px-4 py-3">
      <div className="bg-accent/10 shrink-0 rounded-lg p-2">
        <Icon className="text-accent h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-text text-sm font-semibold">{option.title}</p>
        <p className="text-text-dim truncate text-xs">{option.description}</p>
      </div>
      <FlyoutMenu
        buttonVariant="outline"
        buttonSize="xs"
        autoCloseDelay={0}
        buttonContent={
          <span className="flex items-center gap-1.5">
            Change
            <ChevronDownIcon className="h-3.5 w-3.5 opacity-80" />
          </span>
        }
      >
        {taskTypeMenuItems(onSelect, selectedType)}
      </FlyoutMenu>
    </div>
  )
}
